// @MX:NOTE: [AUTO] china-inapi 전용 공유 axios 인스턴스 — SPEC-CHINA-INAPI-COMPANIES-PRIVILEGES-001 (B1 unit)
// 이 모듈은 ecountEtl.js에 있던 chinaInapiInstance, 인터셉터, 큐 상태를 분리한 것이다.
// B2~B6 화면은 이 모듈에서 named import { chinaInapi }로 사용한다.
// @MX:SPEC: SPEC-CHINA-INAPI-COMPANIES-PRIVILEGES-001

import axios from 'axios';
import store from '@/store';

// china-inapi 전용 axios 인스턴스 (기존 VUE_APP_SERVERURL과 분리)
// @MX:WARN: [AUTO] baseURL 미설정 시 모든 요청 실패 — .env.* 에 VUE_APP_CHINA_INAPI_URL 필수
// @MX:REASON: [AUTO] 단일 baseURL 아키텍처 확정 (v0.2.1). .env 누락 시 빈 상태 표시
const chinaInapiInstance = axios.create({
  baseURL: process.env.VUE_APP_CHINA_INAPI_URL,
  timeout: 60000, // 60초 — sync-trigger proxy 최대 대기 (design.md §6.1)
});

// 401 refresh queue 상태 — chinaInapiInstance 전용, 모듈 스코프에서 관리
let isTokenRefreshing = false;
let failedRequestsQueue = [];

// 요청 인터셉터: JWT Bearer 자동 부착 + Accept-Language 동적 부착
// @MX:NOTE: [AUTO] SPEC-CHINA-INAPI-AUTH-002 B4 — 호출처가 명시한 Authorization 우선 유지
//   (예: /auth/refresh는 refreshToken을 헤더로 보내는데, 인터셉터가 accessToken으로 덮으면 안 됨)
// @MX:NOTE: [AUTO] REQ-LOCALE-P3-FE-001 — Accept-Language 헤더를 store.state.locale.locale로 동적 부착.
//   인터셉터 등록 시점이 아닌 요청 시점에 store를 읽어 locale 변경을 실시간 반영한다.
chinaInapiInstance.interceptors.request.use(
  (config) => {
    // Accept-Language: store 현재 locale (ko | zh). 항상 부착 — Authorization early-return 이전에 설정.
    const locale = store.state?.locale?.locale || 'zh';
    config.headers['Accept-Language'] = locale;

    if (config.headers.Authorization) {
      // 호출처가 이미 Authorization 헤더를 명시한 경우 덮어쓰지 않음
      return config;
    }
    const token = localStorage.getItem('accessTokenManu');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Access-Control-Allow-Origin'] = '*';
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// 응답 인터셉터: 401 refresh queue 처리 (기존 패턴 재사용)
chinaInapiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 토큰 만료 → refresh 후 재시도
    // @MX:WARN: [AUTO] /auth/refresh 자체 401 시 무한 재귀 방지 — SPEC-CHINA-INAPI-AUTH-002 B1
    // @MX:REASON: [AUTO] B4에서 store.requestAccessToken이 chinaInapi.post('/auth/refresh')로 전환되면
    //   refresh 자체 401 시 인터셉터가 다시 requestAccessToken 디스패치 → 무한 루프 발생. 가드 필수.
    if (
      originalRequest.url &&
      !originalRequest.url.includes('/auth/login') &&
      !originalRequest.url.includes('/auth/refresh') &&
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      if (!isTokenRefreshing) {
        isTokenRefreshing = true;
        try {
          await store.dispatch('requestAccessToken');
        } finally {
          isTokenRefreshing = false;
        }

        failedRequestsQueue.forEach((item) => {
          item.resolve(chinaInapiInstance(item.originalRequest));
        });
        failedRequestsQueue = [];

        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('accessTokenManu')}`;
        return chinaInapiInstance(originalRequest);
      } else {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject, originalRequest });
        });
      }
    }

    return Promise.reject(error);
  },
);

// named export: B2~B6 화면에서 import { chinaInapi } from '@/apis/chinaInapi' 형태로 사용
export const chinaInapi = chinaInapiInstance;

// default export: ecountEtl.js 및 기존 소비자 호환
export default chinaInapiInstance;
